/**
 * «Загадки из моих партий» (docs/PATH-V2.md §7.3, docs/path-v2/CONTRACTS.md §3.2) — moments of the child's own games
 * that come back as warm-up tasks. Every motif of stage 1:
 *
 * | motif         | what happened                                         | skill (passed)  | riddle                                    |
 * |---------------|-------------------------------------------------------|-----------------|-------------------------------------------|
 * | `gift`        | a gift was there and not taken                        | gift            | tap / take the gift                       |
 * | `ownHung`     | an own piece ≥ 3 left as a gift                       | safe or save    | 👍/👎 «безопасен?» → a safe move / save it |
 * | `mateMissed`  | a mate in one was there and not played                | check           | give the mate                             |
 * | `mateAllowed` | after the move the opponent mates in one              | check           | a move after which there is no mate       |
 * | `badTrade`    | a capture that gives more than it takes, recaptured   | trade           | 👎 «Это подарок?» on the dashed capture    |
 * | `best`        | «твой лучший ход»: the engine's move that took a gift ≥ 3 or mated | gift | play it again (once a week, ownQueue) |
 *
 * A moment is taken only when ALL hold:
 * - it is the child's move of the main line (a taken-back attempt is not), and a SELF move (§3.10): every move of an
 *   exam, a «Подсказчик» / «тихий учитель» move without a hint asked for it, an advice arrow shown for it, a take-back
 *   offered or accepted at it, or a coach line marked `help: true` right before it; never a move of «Учитель»;
 * - the fact is confirmed: gifts are RECOMPUTED here by `analysis/gifts.ts` (a deterministic material search), mates by
 *   chess.js, a bad trade by the static exchange and the real recapture — never read from the record alone;
 * - it was punished within two half-moves: a missed gift / mate is gone after the opponent's answer; a piece left as a
 *   gift was taken by the answer (or the confirmed engine refutation takes it); the allowed mate was played (or is the
 *   confirmed refutation); the bad trade was recaptured;
 * - its motif is already passed (`OWN_MOTIF_SKILLS`): a mate after the chapter that teaches `check`, a bad trade after
 *   chapter 1.4 (`trade`).
 *
 * At most 2 moments a game, different motifs first (variety), «твой лучший ход» last, then the bigger piece (a mate
 * counts 10), then the earlier move. The review of three moments (../coach/moments.ts) reads the same facts
 * (`moveMoments`) — its missed chance and its mistake are these moments, so the review feeds tomorrow's riddles.
 * Pure: no storage, no engine, no clock.
 */
import { Chess, type Square as ChessSquare } from 'chess.js';
import type { Color, GameEvent, GameRecord, MistakeReason, MoveJudgement, PersonaId, PieceType, SkillId, Square, StageSign } from '@gambit/shared';
import type { TaskSpec } from '@gambit/content';
import { VALUE_PAWNS, parsePlacement, seeCapture, squareIndex, squareName } from '../analysis/board.ts';
import { giftMoves, giftSquares, isGift, isSafeMove, moveGiftFacts, tradedSquareOf } from '../analysis/gifts.ts';
import { movesMeeting, newLosses } from './checks.ts';
import { moveHolds } from './gen/gate.ts';
import { tryApplyMove } from './kingless.ts';

export type OwnMotif = 'gift' | 'ownHung' | 'mateMissed' | 'mateAllowed' | 'badTrade' | 'best';

export interface OwnRiddle {
  /** `<gameId>:<ply>` — stable, the attempts history keys by it */
  id: string;
  gameId: string;
  /** the child's move (1-based ply of the main line) */
  ply: number;
  /** the bot of that game — the frame line «Загадка из партии с …» */
  persona: PersonaId;
  /** the position before the child's move (the riddle is asked here) */
  fen: string;
  kind: 'tap' | 'move' | 'yesno';
  task: TaskSpec;
  /** after a 👍/👎 on the played move: «найди безопасный ход» */
  then?: TaskSpec;
  motif: OwnMotif;
  /** the move the child played (UCI) — shown dashed in a yes/no */
  played: string;
  /** squares of the motif: the gifts he could take / his pieces left as gifts / the king that is mated */
  targets: Square[];
  /** squares of pieces that have nothing to do with the motif — dimmed the first time (§7.3) */
  dim: Square[];
  /** the frame line after the intro (absent = `OWN_NOTICED_POOL` «Мы тогда не заметили…»; «лучший ход» has its own) */
  frame?: string;
  /** the game's end (ISO) — the riddle is first due the day after */
  createdAt: string;
  /** the child lost that game — its riddles come first (§7.2 «проигранные партии разбираются первыми», fixer r1) */
  lost?: true;
}

export interface OwnOptions {
  /** defaults to the record's `childColor` */
  childColor?: Color;
  /** skills the child has passed on the path (only their motifs are asked) */
  stagePassedSkills: readonly SkillId[];
}

/** skill(s) that must be passed for a motif (any of them) */
export const OWN_MOTIF_SKILLS: Readonly<Record<OwnMotif, readonly SkillId[]>> = {
  gift: ['gift'],
  ownHung: ['safe', 'save'],
  mateMissed: ['check'],
  mateAllowed: ['check'],
  badTrade: ['trade'],
  best: ['gift'],
};
/**
 * The generator recipe of «похожие позиции» after a clean solve (§7.3, §11.2; ids of CONTRACTS v1.2); null = the motif
 * has no recipe of stage 1 — the moment itself comes back ~3 weeks later instead.
 */
export const OWN_MOTIF_RECIPE: Readonly<Record<OwnMotif, string | null>> = {
  gift: 'gift@1',
  ownHung: 'safe@2',
  mateMissed: null,
  mateAllowed: null,
  badTrade: 'trade@1',
  best: null,
};
export const OWN_PER_GAME = 2;
/**
 * The rules a cached riddle was made by (the web's cache `gambit.path.own` makes a game's riddles again under another
 * version): 2 = review round 3 — an even trade is no dropped piece, «найди безопасный ход» / «найди ход без мата»
 * accept only moves that keep our material, each wrong try carries its reason.
 */
export const OWN_RIDDLE_RULES = 2;
/** a piece the child can leave «as a gift» for the motif: knight or more (moveGiftFacts.ownHung) */
const OWN_HUNG_MIN = 3;
/** «твой лучший ход»: a gift of a piece (≥ 3) or a mate */
const BEST_MIN_WORTH = 3;
/** a bad trade gives at least this much more than it takes (centipawns, static exchange) */
const BAD_TRADE_CP = -200;
/** a mate outranks every piece */
const MATE_WORTH = 10;

// ───────────────────────── prompts ─────────────────────────

/**
 * The prompts of a riddle's tasks. A gift riddle says the chapter's instructions (packages/content chapters/gifts.ts
 * `v3.path.gift.find` / `gift.take`), a bad trade its question `v3.path.gift.ask` («Это подарок?») — the same words,
 * recorded once. The other motifs have their own prompts in packages/content lines.own.ts (`v3.path.own.ask.*`).
 */
export const OWN_PROMPTS = {
  tapGift: 'v3.path.gift.find',
  takeGift: 'v3.path.gift.take',
  isSafe: 'v3.path.own.ask.safe',
  findSafe: 'v3.path.own.ask.findSafe',
  save: 'v3.path.own.ask.save',
  mate: 'v3.path.own.ask.mate',
  noMate: 'v3.path.own.ask.noMate',
  trade: 'v3.path.gift.ask',
  best: 'v3.path.own.ask.best',
} as const;

/** «Твой лучший ход» — the frame said instead of «мы не заметили». */
export const OWN_BEST_FRAME = 'v3.path.own.best';

// ───────────────────────── the game, move by move ─────────────────────────

export interface MainMove {
  uci: string;
  san: string;
  color: Color;
  fenBefore: string;
  fenAfter: string;
  captured?: PieceType;
}

/** A PGN longer than this (chars) is not counted: it holds far more plies than any game a cap would accept. */
const PGN_COUNT_MAX_CHARS = 64 * 1024;

/**
 * The plies of a PGN's main line counted from its text, without replaying it: tags, comments, variations, NAGs, move
 * numbers and the result are left out (`Infinity` for a PGN past `PGN_COUNT_MAX_CHARS`). For a cheap length cap before a
 * replay — the server's exam check (review round 3: a 600-ply record was replayed only to be refused for its length).
 */
export function pgnPlyCount(pgn: string): number {
  if (pgn.length > PGN_COUNT_MAX_CHARS) return Number.POSITIVE_INFINITY;
  let text = pgn.replace(/^\s*\[[^\]\n]*\]\s*$/gmu, ' ').replace(/\{[^}]*\}/gu, ' ').replace(/;[^\n]*/gu, ' ');
  for (let prev = ''; prev !== text; ) {
    prev = text;
    text = text.replace(/\([^()]*\)/gu, ' ');
  }
  let plies = 0;
  for (const raw of text.split(/\s+/u)) {
    const token = raw.replace(/^\d+\.+/u, '');
    if (token === '' || /^\$\d+$/u.test(token) || /^(?:1-0|0-1|1\/2-1\/2|\*)$/u.test(token)) continue;
    plies += 1;
  }
  return plies;
}

/** The main line of the record's PGN (taken-back attempts are not in it); [] when the PGN does not parse. */
export function mainLine(record: Pick<GameRecord, 'pgn'>): MainMove[] {
  const chess = new Chess();
  try {
    chess.loadPgn(record.pgn);
  } catch {
    return [];
  }
  return chess.history({ verbose: true }).map((m) => ({
    uci: `${m.from}${m.to}${m.promotion ?? ''}`,
    san: m.san,
    color: m.color,
    fenBefore: m.before,
    fenAfter: m.after,
    ...(m.captured ? { captured: m.captured } : {}),
  }));
}

/**
 * Is the child's move at `ply` a self move (§3.10)? How help is recorded in a GameRecord:
 * - `coachStyle` (absent in older records: `examMode` → exam, else «Подсказчик»);
 * - `hintRequested` / `hintGiven` events carry the ply of the move they were for (gameStore `produceHint`);
 * - the child's `move` event carries `data.advice` (SAN list) when an advice arrow was shown for it;
 * - `takebackOffered` / `takebackAccepted` carry the ply of the child's move (the move at that ply after them was
 *   made with the coach's warning);
 * - a coach line marked `data.help === true` at the ply before (a «тихий учитель» danger / gift moment — proposed
 *   marker, see the report) counts as help too.
 */
export function isSelfMove(record: Pick<GameRecord, 'coachStyle' | 'examMode' | 'events'>, ply: number): boolean {
  const style = record.coachStyle ?? (record.examMode ? 'exam' : 'helper');
  if (style === 'teacher') return false;
  if (style === 'exam') return true;
  return !record.events.some((e) => helpedAt(e, ply));
}

/**
 * Was the move at `ply` made without help shown FOR it (no arrow, no hint, no take-back, no help line) — whatever the
 * style? A «Учитель» move the child found before any arrow came is his own here (the review may praise it), while
 * `isSelfMove` never counts a teacher game (the metrics, §3.10).
 */
export function unaidedMove(record: Pick<GameRecord, 'events'>, ply: number): boolean {
  return !record.events.some((e) => helpedAt(e, ply));
}

function helpedAt(e: GameEvent, ply: number): boolean {
  switch (e.type) {
    case 'hintRequested':
    case 'hintGiven':
    case 'takebackOffered':
    case 'takebackAccepted':
      return e.ply === ply;
    case 'move':
      return e.ply === ply && Array.isArray(e.data.advice) && e.data.advice.length > 0;
    case 'coachSaid':
      return (e.ply === ply || e.ply === ply - 1) && e.data.help === true;
    default:
      return false;
  }
}

// ───────────────────────── facts ─────────────────────────

function pieceAt(fen: string, sq: string): { type: PieceType; color: Color } | null {
  try {
    const p = new Chess(fen).get(sq as ChessSquare);
    return p ? { type: p.type, color: p.color } : null;
  } catch {
    return null;
  }
}

function targetOf(uci: string): Square {
  return uci.slice(2, 4) as Square;
}

function legalUcis(fen: string): string[] {
  try {
    return new Chess(fen).moves({ verbose: true }).map((m) => `${m.from}${m.to}${m.promotion ?? ''}`);
  } catch {
    return [];
  }
}

function afterMove(fen: string, uci: string): Chess | null {
  try {
    const c = new Chess(fen);
    c.move({ from: uci.slice(0, 2), to: uci.slice(2, 4), ...(uci.length > 4 ? { promotion: uci[4] } : {}) });
    return c;
  } catch {
    return null;
  }
}

/**
 * The SAN moves of the board's side to move that give mate: chess.js marks them «#» in ONE plain move generation (no
 * board per move, and no verbose Move objects — they build two FENs each: the review and the riddles scan every child
 * move of a game).
 */
function matingSans(board: Chess): string[] {
  return board.moves().filter((san) => san.endsWith('#'));
}

/** The moves of the side to move that give mate at once (UCI). */
export function mateInOneMoves(fen: string): string[] {
  let board: Chess;
  try {
    board = new Chess(fen);
  } catch {
    return [];
  }
  return matingSans(board).map((san) => {
    const m = board.move(san);
    board.undo();
    return `${m.from}${m.to}${m.promotion ?? ''}`;
  });
}

/** After the move `uci`, can the opponent mate in one? (false for an illegal move) */
export function allowsMateInOne(fen: string, uci: string): boolean {
  const after = afterMove(fen, uci);
  return after !== null && matingSans(after).length > 0;
}

/** Does the move give mate? */
function mates(fen: string, uci: string): boolean {
  return afterMove(fen, uci)?.isCheckmate() === true;
}

/** Static exchange of the capture `uci` (centipawns, mover's view); 0 for a quiet move. */
export function captureExchange(fen: string, uci: string): number {
  const board = parsePlacement(fen);
  const from = squareIndex(uci.slice(0, 2));
  const to = squareIndex(uci.slice(2, 4));
  if (from < 0 || to < 0 || !board[to]) return 0;
  return seeCapture(board, from, to);
}

/** The square of the king of `color` in `fen`. */
function kingSquare(fen: string, color: Color): Square | null {
  const board = parsePlacement(fen);
  for (let i = 0; i < 64; i++) {
    const p = board[i];
    if (p && p.type === 'k' && p.color === color) return squareName(i);
  }
  return null;
}

// ───────────────────────── moments ─────────────────────────

/** A confirmed, punished moment of one move before the self / skills filters and the per-game cap. */
export interface OwnMoment {
  motif: OwnMotif;
  ply: number;
  judgement: MoveJudgement;
  targets: Square[];
  /** value (pawns) of the biggest target — the selection prefers bigger pieces (a mate counts 10) */
  worth: number;
  /** the move that would have been right here (a gift / a mate / the engine's move) — the review proposes it */
  better?: string;
  /** the opponent's answer that punished it (a red arrow in the review) */
  reply?: string;
  /**
   * the game's own answer did it (false: the bot left the piece / the mate alone and only the confirmed engine
   * refutation does it — the review then says «могли забрать», never «забрали», §7.2: the trainer misses ~10 %)
   */
  inGame: boolean;
}

/**
 * Every motif of ONE child move of the main line (the facts only: no self filter, no skills filter): the same rules
 * for the riddles and the review (../coach/moments.ts). `reply` = the opponent's answer on the main line, if any.
 * `opts.mates: false` skips the mate scans (a riddle refresh before skill `check`: those motifs cannot be asked).
 */
export function moveMoments(j: MoveJudgement, played: MainMove, reply: MainMove | undefined, opts: { mates?: boolean } = {}): OwnMoment[] {
  const scanMates = opts.mates !== false;
  const out: OwnMoment[] = [];
  let facts: ReturnType<typeof moveGiftFacts>;
  try {
    facts = moveGiftFacts(j.fenBefore, j.uci);
  } catch {
    return out;
  }
  const mateNow = scanMates ? mateInOneMoves(j.fenBefore) : [];
  const gaveMate = played.san.endsWith('#');

  // a mate in one was there and was not played — and the chance is gone after the answer
  if (mateNow.length > 0 && !gaveMate && reply && mateInOneMoves(reply.fenAfter).length === 0) {
    const king = kingSquare(j.fenBefore, j.color === 'w' ? 'b' : 'w');
    out.push({ motif: 'mateMissed', ply: j.ply, judgement: j, targets: king ? [king] : [], worth: MATE_WORTH, better: mateNow[0] as string, reply: reply.uci, inGame: true });
  }

  // did not take a gift — and the opponent's answer took it away
  if (facts.giftAvailable && !facts.giftTaken && reply) {
    const gifts = giftMoves(j.fenBefore);
    const stillThere = new Set(giftMoves(reply.fenAfter));
    if (gifts.length > 0 && !gifts.some((g) => stillThere.has(g))) {
      const targets = [...new Set(gifts.map(targetOf))].sort();
      const valueOf = (g: string): number => VALUE_PAWNS[pieceAt(j.fenBefore, targetOf(g))?.type ?? 'p'];
      const worth = Math.max(...targets.map((sq) => VALUE_PAWNS[pieceAt(j.fenBefore, sq)?.type ?? 'p']));
      const better = [...gifts].sort((a, b) => valueOf(b) - valueOf(a))[0];
      out.push({ motif: 'gift', ply: j.ply, judgement: j, targets, worth, ...(better ? { better } : {}), reply: reply.uci, inGame: true });
    }
  }

  // after the move the opponent mates in one — and he did (or the confirmed refutation does)
  const theirMates = scanMates ? mateInOneMoves(played.fenAfter) : [];
  if (theirMates.length > 0) {
    const mated = reply !== undefined && theirMates.includes(reply.uci);
    const refuted = j.confidence === 'confirmed' && j.refutationPvUci[0] !== undefined && theirMates.includes(j.refutationPvUci[0]);
    if (mated || refuted) {
      const king = kingSquare(played.fenAfter, j.color);
      const by = mated ? (reply as MainMove).uci : (j.refutationPvUci[0] as string);
      out.push({ motif: 'mateAllowed', ply: j.ply, judgement: j, targets: king ? [king] : [], worth: MATE_WORTH, reply: by, ...(j.bestUci ? { better: j.bestUci } : {}), inGame: mated });
    }
  }

  // left an own piece ≥ 3 as a gift — and the opponent took it (or the confirmed refutation does); the piece that has
  // just captured at least its own worth is a trade, not a gift (review round 3: Bxc6 dxc6 «Нашу фигуру забрали даром»)
  if (facts.ownHung) {
    const traded = played.captured ? tradedSquareOf(j.fenBefore, j.uci) : null;
    const hung = giftMoves(played.fenAfter).filter((g) => targetOf(g) !== traded && VALUE_PAWNS[pieceAt(played.fenAfter, targetOf(g))?.type ?? 'p'] >= OWN_HUNG_MIN);
    const taken = reply !== undefined && hung.includes(reply.uci);
    const refuted = j.confidence === 'confirmed' && j.refutationPvUci[0] !== undefined && hung.includes(j.refutationPvUci[0]);
    if (hung.length > 0 && (taken || refuted)) {
      const targets = [...new Set(hung.map(targetOf))].sort();
      const worth = Math.max(...targets.map((sq) => VALUE_PAWNS[pieceAt(played.fenAfter, sq)?.type ?? 'p']));
      out.push({ motif: 'ownHung', ply: j.ply, judgement: j, targets, worth, reply: taken ? (reply as MainMove).uci : (j.refutationPvUci[0] as string), ...(j.bestUci ? { better: j.bestUci } : {}), inGame: taken });
    }
  }

  // a capture that gives more than it takes — and the opponent took back on that square
  if (played.captured && !facts.giftTaken && reply && targetOf(reply.uci) === targetOf(j.uci) && captureExchange(j.fenBefore, j.uci) <= BAD_TRADE_CP) {
    const mover = pieceAt(j.fenBefore, j.uci.slice(0, 2));
    out.push({ motif: 'badTrade', ply: j.ply, judgement: j, targets: [targetOf(j.uci)], worth: VALUE_PAWNS[mover?.type ?? 'p'], reply: reply.uci, ...(j.bestUci ? { better: j.bestUci } : {}), inGame: true });
  }

  // «твой лучший ход»: the engine's own move (or no better one), and it took a piece as a gift or gave mate
  const engineMove = j.uci === j.bestUci || j.classification === 'best';
  if (engineMove) {
    const taken = facts.giftTaken ? VALUE_PAWNS[played.captured ?? 'p'] : 0;
    const worth = gaveMate ? MATE_WORTH : taken;
    if (worth >= BEST_MIN_WORTH) out.push({ motif: 'best', ply: j.ply, judgement: j, targets: [targetOf(j.uci)], worth, inGame: true });
  }
  return out;
}

/** The child's judged moves of the main line with their answer (taken-back attempts and stale records left out). */
export function childMoves(record: GameRecord, childColor: Color = record.childColor): { j: MoveJudgement; played: MainMove; reply: MainMove | undefined }[] {
  const line = mainLine(record);
  const out: { j: MoveJudgement; played: MainMove; reply: MainMove | undefined }[] = [];
  const seen = new Set<number>();
  for (const j of [...record.judgements].sort((a, b) => a.ply - b.ply)) {
    if (seen.has(j.ply)) continue;
    const played = line[j.ply - 1];
    // a taken-back attempt (not the main line), a move of the other side, a stale record
    if (!played || played.uci !== j.uci || played.color !== childColor || played.fenBefore !== j.fenBefore) continue;
    seen.add(j.ply);
    out.push({ j, played, reply: line[j.ply] });
  }
  return out;
}

/**
 * Every moment of the game that passes the self / confirmed / punished rules (§7.3), in move order. Several motifs may
 * come from one move; the selection keeps one per move.
 */
export function ownMoments(record: GameRecord, opts: { childColor?: Color; mates?: boolean } = {}): OwnMoment[] {
  const mates = opts.mates === false ? { mates: false } : {};
  return childMoves(record, opts.childColor ?? record.childColor).flatMap(({ j, played, reply }) => (isSelfMove(record, j.ply) ? moveMoments(j, played, reply, mates) : []));
}

function motifPassed(motif: OwnMotif, passed: readonly SkillId[]): boolean {
  return OWN_MOTIF_SKILLS[motif].some((s) => passed.includes(s));
}

// ───────────────────────── tasks ─────────────────────────

function occupied(fen: string): Square[] {
  const out: Square[] = [];
  for (const row of new Chess(fen).board()) for (const cell of row) if (cell) out.push(cell.square);
  return out;
}

function dimExcept(fen: string, keep: Iterable<string>): Square[] {
  const k = new Set(keep);
  return occupied(fen).filter((sq) => !k.has(sq)).sort();
}

/**
 * EVERY move after which the opponent has no mate in one (§3.5 p.20: any right answer counts) — the safe ones first,
 * so a shown answer is also a safe move. One board, move / undo.
 */
export function movesWithoutMate(fen: string): string[] {
  let board: Chess;
  try {
    board = new Chess(fen);
  } catch {
    return [];
  }
  const noMate: string[] = [];
  for (const san of board.moves()) {
    const m = board.move(san);
    const mate = matingSans(board).length > 0;
    board.undo();
    if (!mate) noMate.push(`${m.from}${m.to}${m.promotion ?? ''}`);
  }
  const safe = (uci: string): boolean => {
    try {
      return isSafeMove(fen, uci);
    } catch {
      return false;
    }
  };
  return [...noMate.filter(safe), ...noMate.filter((u) => !safe(u))];
}

/**
 * Why a move is no answer to a riddle about keeping our pieces (review round 3: «найди безопасный ход» accepted every
 * move that left no gift — the queen onto a defended square, a move into a mate in one), null = it is one:
 * - never a mate in one for the opponent (`allowsMate`, the red arrow shows the mate);
 * - its material holds against captures and checks (gen/gate.ts `moveHolds`: ≤ 1 pawn, no mate);
 * - `strict` («найди безопасный ход»): nothing of ours is left as a gift (`isSafeMove`; the piece that has just
 *   captured at least its own worth excepted — an even trade is no gift) and nothing is newly lost in an exchange
 *   (checks.ts `newLosses`, the same exception).
 * The reason of a wrong try says what it loses: the moved piece where it went `willBeTaken`, another piece `notSaved`.
 */
function riddleLoss(fen: string, uci: string, strict: boolean): MistakeReason | null {
  if (allowsMateInOne(fen, uci)) return 'allowsMate';
  const after = tryApplyMove(fen, uci);
  if (after === null) return 'other';
  const traded = tradedSquareOf(fen, uci);
  const lost = (): Square[] => newLosses(fen, uci, after).filter((sq) => sq !== traded);
  const lossOf = (squares: readonly Square[]): MistakeReason => (squares.includes(uci.slice(2, 4)) ? 'willBeTaken' : 'notSaved');
  if (strict) {
    const now = lost();
    if (now.length > 0) return lossOf(now);
    let safe = false;
    try {
      safe = isSafeMove(fen, uci, { trades: true });
    } catch {
      safe = false;
    }
    if (!safe) return 'notSaved';
  }
  if (!moveHolds(fen, uci)) return lossOf(strict ? [] : lost());
  return null;
}

/** Every legal move split by `riddleLoss`: the answers, and the reason of every other move (a riddle's `why`). */
function riddleAnswers(fen: string, strict: boolean): { moves: string[]; why: Record<string, MistakeReason> } {
  const moves: string[] = [];
  const why: Record<string, MistakeReason> = {};
  for (const u of legalUcis(fen)) {
    const r = riddleLoss(fen, u, strict);
    if (r === null) moves.push(u);
    else why[u] = r;
  }
  return { moves, why };
}

/**
 * The riddle of a moment, or null when it cannot be asked truthfully (e.g. no safe move exists).
 * - 'gift': tap the gift (the first gift riddle of a game) or take it (the second) — a little variety;
 * - 'ownHung': 👍/👎 «Этот ход безопасен?» on the played move shown dashed, then «найди безопасный ход»; when the
 *   piece was already a gift before the move (the played move is «safe» by `isSafeMove`), «спаси фигуру» instead;
 * - 'mateMissed': give the mate (any mate counts); 'mateAllowed': a move after which there is no mate and nothing of
 *   ours is lost (`riddleLoss`; dashed = played) — a try that allows the mate says so and shows it (`allowsMate`);
 * - 'badTrade': 👎 on the dashed capture to «Это подарок?» (`isGift` → no: «мы тоже отдаём»);
 * - 'best': play the strong move again (another mate / an equal gift counts too), with its own frame line.
 */
export function riddleOfMoment(record: GameRecord, m: OwnMoment, giftOrdinal = 0): OwnRiddle | null {
  const j = m.judgement;
  const fen = j.fenBefore;
  const id = `${record.id}:${m.ply}`;
  const key = `own:${id}`;
  const lost = (record.result === '1-0' && record.childColor === 'b') || (record.result === '0-1' && record.childColor === 'w');
  const base = { id, gameId: record.id, ply: m.ply, persona: record.personaId, fen, motif: m.motif, played: j.uci, targets: m.targets, createdAt: record.endedAt, ...(lost ? { lost: true as const } : {}) };
  const from = j.uci.slice(0, 2) as Square;
  const to = j.uci.slice(2, 4) as Square;
  const dashed: StageSign = { kind: 'dashed', from, to };

  switch (m.motif) {
    case 'gift': {
      const gifts = giftMoves(fen);
      const keep = [...m.targets, ...gifts.map((g) => g.slice(0, 2))];
      if (giftOrdinal % 2 === 0) {
        const task: TaskSpec = { key, kind: 'tap', fen, skill: 'gift', tier: 2, prompt: OWN_PROMPTS.tapGift, label: 'Подарок', rule: { rule: 'gift' }, expect: giftSquares(fen) };
        return { ...base, kind: 'tap', task, dim: dimExcept(fen, keep) };
      }
      const task: TaskSpec = { key, kind: 'move', fen, skill: 'gift', tier: 2, prompt: OWN_PROMPTS.takeGift, label: 'Забери', rule: { rule: 'takesGift' }, expect: gifts };
      return { ...base, kind: 'move', task, dim: dimExcept(fen, keep) };
    }
    case 'mateMissed': {
      const matesNow = mateInOneMoves(fen);
      if (matesNow.length === 0) return null;
      const keep = new Set<string>([...m.targets, ...matesNow.map((u) => u.slice(0, 2))]);
      const task: TaskSpec = { key, kind: 'move', fen, skill: 'check', tier: 2, prompt: OWN_PROMPTS.mate, label: 'Мат', rule: { rule: 'mate' }, expect: matesNow };
      return { ...base, kind: 'move', task, dim: dimExcept(fen, keep) };
    }
    case 'mateAllowed': {
      const { moves: ok, why } = riddleAnswers(fen, false);
      if (ok.length === 0 || ok.includes(j.uci)) return null;
      const keep = new Set<string>([from, to, ...m.targets, ...(m.reply ? [m.reply.slice(0, 2)] : [])]);
      const task: TaskSpec = { key, kind: 'move', fen, skill: 'check', tier: 2, prompt: OWN_PROMPTS.noMate, label: 'Без мата', signs: [dashed], rule: { rule: 'uci', moves: ok }, expect: ok, why };
      return { ...base, kind: 'move', task, dim: dimExcept(fen, keep) };
    }
    case 'badTrade': {
      let gift = true;
      try {
        gift = isGift(fen, j.uci);
      } catch {
        gift = true;
      }
      if (gift) return null;
      const keep = new Set<string>([from, to, ...(m.reply ? [m.reply.slice(0, 2)] : [])]);
      const task: TaskSpec = { key, kind: 'yesno', fen, skill: 'trade', tier: 2, prompt: OWN_PROMPTS.trade, label: 'Подарок?', signs: [dashed], rule: { rule: 'isGift', move: j.uci }, expect: 'no' };
      return { ...base, kind: 'yesno', task, dim: dimExcept(fen, keep) };
    }
    case 'best': {
      const keep = new Set<string>([from, to, ...m.targets]);
      if (mates(fen, j.uci)) {
        const task: TaskSpec = { key, kind: 'move', fen, skill: 'gift', tier: 2, prompt: OWN_PROMPTS.best, label: 'Твой ход', rule: { rule: 'mate' }, expect: mateInOneMoves(fen) };
        return { ...base, kind: 'move', task, dim: dimExcept(fen, keep), frame: OWN_BEST_FRAME };
      }
      const worth = VALUE_PAWNS[pieceAt(fen, to)?.type ?? 'p'];
      const equal = giftMoves(fen).filter((g) => VALUE_PAWNS[pieceAt(fen, targetOf(g))?.type ?? 'p'] >= worth);
      const moves = [...new Set([j.uci, ...equal])];
      const task: TaskSpec = { key, kind: 'move', fen, skill: 'gift', tier: 2, prompt: OWN_PROMPTS.best, label: 'Твой ход', rule: { rule: 'uci', moves }, expect: moves };
      return { ...base, kind: 'move', task, dim: dimExcept(fen, keep), frame: OWN_BEST_FRAME };
    }
    case 'ownHung':
      return hungRiddle(m, fen, key, base, dashed, from, to);
  }
}

function hungRiddle(m: OwnMoment, fen: string, key: string, base: Omit<OwnRiddle, 'kind' | 'task' | 'dim'>, dashed: StageSign, from: Square, to: Square): OwnRiddle | null {
  const j = m.judgement;
  const after = afterMove(fen, j.uci);
  if (!after) return null;
  const attackers = giftMoves(after.fen())
    .filter((g) => m.targets.includes(targetOf(g)))
    .map((g) => g.slice(0, 2));
  const keep = new Set<string>([from, to, ...m.targets, ...attackers]);
  // an even trade is no gift (review round 3): the played move is «unsafe» only by what it really leaves
  if (!isSafeMove(fen, j.uci, { trades: true })) {
    // «найди безопасный ход»: only moves that keep our material (`riddleLoss`) — none, no riddle
    const { moves: safe, why } = riddleAnswers(fen, true);
    if (safe.length === 0) return null;
    // a 👍: the dashed move loses the piece it moved («сюда нельзя») or another one («нашу фигуру заберут»)
    const yes: MistakeReason = m.targets.includes(to) ? 'willBeTaken' : 'notSaved';
    const task: TaskSpec = { key, kind: 'yesno', fen, skill: 'safe', tier: 2, prompt: OWN_PROMPTS.isSafe, label: 'Безопасно?', signs: [dashed], rule: { rule: 'isSafe', move: j.uci }, expect: 'no', why: { yes } };
    const then: TaskSpec = { key: `${key}/safe`, kind: 'move', fen, skill: 'safe', tier: 2, prompt: OWN_PROMPTS.findSafe, label: 'Безопасный ход', rule: { rule: 'uci', moves: safe }, expect: safe, why };
    return { ...base, kind: 'yesno', task, then, dim: dimExcept(fen, keep) };
  }
  // the piece was a gift already before the move: «спаси фигуру» on the biggest one that stood there
  const square = m.targets.find((sq) => pieceAt(fen, sq)?.color === j.color);
  if (!square) return null;
  // checks.ts `saves` (v1-H): the piece is no longer lost and nothing else of ours is newly lost — a real game may have
  // no such move (two pieces hang, a guard is pinned): then the riddle cannot be asked truthfully
  const saves = movesMeeting(fen, { rule: 'saves', square });
  if (saves.length === 0 || saves.includes(j.uci)) return null;
  const task: TaskSpec = { key, kind: 'move', fen, skill: 'save', tier: 2, prompt: OWN_PROMPTS.save, label: 'Спаси', rule: { rule: 'saves', square }, expect: saves };
  return { ...base, kind: 'move', task, dim: dimExcept(fen, keep) };
}

/** Riddles of one game (§7.3): ≤ 2, only self moves, confirmed, punished, of passed motifs. */
export function riddlesFromGame(record: GameRecord, opts: OwnOptions): OwnRiddle[] {
  // the mate motifs only once they can be asked (the scans are the bulk of the work)
  const mates = motifPassed('mateMissed', opts.stagePassedSkills) || motifPassed('mateAllowed', opts.stagePassedSkills);
  const moments = ownMoments(record, { ...(opts.childColor ? { childColor: opts.childColor } : {}), mates }).filter((m) => motifPassed(m.motif, opts.stagePassedSkills));
  return riddlesOfMoments(record, moments);
}

/**
 * Selection + tasks over already filtered moments: ≤ `max` riddles, one per move; a motif not chosen yet first
 * (variety), «твой лучший ход» only when nothing else is left, then the bigger piece, then the earlier move. A moment
 * that cannot be asked gives its place to the next.
 */
export function riddlesOfMoments(record: GameRecord, moments: readonly OwnMoment[], max = OWN_PER_GAME): OwnRiddle[] {
  const ranked = [...moments].sort((a, b) => Number(a.motif === 'best') - Number(b.motif === 'best') || b.worth - a.worth || a.ply - b.ply);
  const tried = new Set<OwnMoment>();
  const out: OwnRiddle[] = [];
  while (out.length < max) {
    const open = ranked.filter((m) => !tried.has(m) && !out.some((r) => r.ply === m.ply));
    const m = open.find((x) => x.motif !== 'best' && !out.some((r) => r.motif === x.motif)) ?? open.find((x) => x.motif !== 'best') ?? open[0];
    if (!m) break;
    tried.add(m);
    const r = riddleOfMoment(record, m, out.filter((x) => x.motif === 'gift').length);
    if (r) out.push(r);
  }
  return out.sort((a, b) => a.ply - b.ply);
}

/** Is a riddle's motif among the passed skills (the web filters cached riddles with it). */
export function ownMotifPassed(motif: OwnMotif, passed: readonly SkillId[]): boolean {
  return motifPassed(motif, passed);
}

/** Bots of stage 1 that have their own intro pool (the clip catalogue has no persona placeholder). */
const OWN_INTRO_PERSONAS: readonly PersonaId[] = ['petya', 'sonya', 'grisha'];

/** The frame line pool of a riddle (packages/content/src/path/lines.own.ts): «Загадка из партии с Соней!». */
export function ownIntroPool(persona: PersonaId): string {
  return OWN_INTRO_PERSONAS.includes(persona) ? `v3.path.own.intro.${persona}` : 'v3.path.own.intro';
}

/** «Мы тогда не заметили…» — said after the intro (never «ошибка»). */
export const OWN_NOTICED_POOL = 'v3.path.own.noticed';

/** The second frame line of a riddle: «мы не заметили», or the one of «твой лучший ход». */
export function ownFramePool(riddle: Pick<OwnRiddle, 'frame'>): string {
  return riddle.frame ?? OWN_NOTICED_POOL;
}
